/**
 * DUBAI AROMA - PREMIUM PDP JAVASCRIPT
 * Handles all interactive functionality for the product detail page
 */

class DubaiAromaPDP {
  constructor() {
    this.productData = null;
    this.currentImageIndex = 0;
    this.cartQuantity = 1;
    this.selectedFBTProducts = [];
    this.activityToastTimer = null;
    this.exitIntentShown = false;
    
    this.init();
  }

  /**
   * Initialize all functionality
   */
  init() {
    this.loadProductData();
    this.initGallery();
    this.initQuantitySelector();
    // Primary purchase submission is handled by pdp-conversion.js.
    this.initWishlist();
    this.initFragranceNotes();
    this.initPerformanceBars();
    this.initFBT();
    this.initStickyCart();
    this.initActivityToast();
    this.initExitIntent();
    this.renderBadges();
  }

  /**
   * Load product data from Shopify or data attributes
   */
  loadProductData() {
    const productDataElement = document.getElementById('pdp-product-data');
    if (productDataElement) {
      try {
        this.productData = JSON.parse(productDataElement.textContent);
      } catch (e) {
        console.error('Failed to parse product data:', e);
        this.productData = this.getFallbackData();
      }
    } else {
      this.productData = this.getFallbackData();
    }
  }

  /**
   * Get fallback data structure
   */
  getFallbackData() {
    return {
      inventory: {
        count: 0,
        salesThisWeek: 0,
        isBestseller: false,
        isNewArrival: false,
        daysOld: 0
      },
      reviews: [],
      frequentlyBoughtTogether: [],
      relatedProducts: [],
      recentActivity: []
    };
  }

  /**
   * Initialize image gallery
   */
  initGallery() {
    const thumbnails = document.querySelectorAll('.pdp-gallery__thumbnail');
    const mainImage = document.querySelector('.pdp-gallery__main-image');
    
    thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => {
        this.switchImage(index, mainImage, thumbnails);
      });
    });
  }

  /**
   * Switch main image with fade effect
   */
  switchImage(index, mainImage, thumbnails) {
    if (!mainImage) return;
    
    const newSrc = thumbnails[index]?.dataset.fullSrc || thumbnails[index]?.querySelector('img')?.src;
    if (!newSrc) return;
    
    // Fade out
    mainImage.classList.add('fade-out');
    
    setTimeout(() => {
      mainImage.src = newSrc;
      mainImage.classList.remove('fade-out');
      
      // Update active thumbnail
      thumbnails.forEach(t => t.classList.remove('active'));
      thumbnails[index].classList.add('active');
      
      this.currentImageIndex = index;
    }, 200);
  }

  /**
   * Initialize quantity selector
   */
  initQuantitySelector() {
    const minusBtn = document.querySelector('.pdp-quantity__btn[data-action="minus"]');
    const plusBtn = document.querySelector('.pdp-quantity__btn[data-action="plus"]');
    const input = document.querySelector('.pdp-quantity__input');
    
    if (minusBtn) {
      minusBtn.addEventListener('click', () => this.updateQuantity(-1));
    }
    
    if (plusBtn) {
      plusBtn.addEventListener('click', () => this.updateQuantity(1));
    }
    
    if (input) {
      input.addEventListener('change', (e) => {
        const value = parseInt(e.target.value) || 1;
        this.cartQuantity = Math.max(1, Math.min(value, 99));
        input.value = this.cartQuantity;
        this.updateStickyCartQty();
      });
    }
  }

  /**
   * Update quantity
   */
  updateQuantity(change) {
    const input = document.querySelector('.pdp-quantity__input');
    if (!input) return;
    
    this.cartQuantity = Math.max(1, Math.min(this.cartQuantity + change, 99));
    input.value = this.cartQuantity;
    this.updateStickyCartQty();
  }

  /**
   * Update sticky cart quantity display
   */
  updateStickyCartQty() {
    const stickyQtyValue = document.querySelector('.pdp-sticky-cart__qty-value');
    if (stickyQtyValue) {
      stickyQtyValue.textContent = this.cartQuantity;
    }
  }

  /**
   * Initialize Add to Cart functionality
   */
  initAddToCart() {
    const addToCartBtn = document.querySelector('.pdp-btn-add-to-cart');
    const stickyAddBtn = document.querySelector('.pdp-sticky-cart__cta');
    
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', (e) => this.addToCart(e));
    }
    
    if (stickyAddBtn) {
      stickyAddBtn.addEventListener('click', (e) => this.addToCart(e));
    }
    
    // Sticky cart quantity controls
    const stickyMinus = document.querySelector('.pdp-sticky-cart__qty-btn[data-action="minus"]');
    const stickyPlus = document.querySelector('.pdp-sticky-cart__qty-btn[data-action="plus"]');
    
    if (stickyMinus) {
      stickyMinus.addEventListener('click', () => this.updateQuantity(-1));
    }
    
    if (stickyPlus) {
      stickyPlus.addEventListener('click', () => this.updateQuantity(1));
    }
  }

  /**
   * Add product to cart
   */
  async addToCart(e) {
    e.preventDefault();
    
    const btn = e.target;
    const originalText = btn.textContent;
    
    // Get variant ID from form or data attribute
    const variantId = document.querySelector('[name="id"]')?.value;
    
    if (!variantId) {
      console.error('No variant ID found');
      return;
    }
    
    // Set loading state
    btn.classList.add('loading');
    btn.disabled = true;
    btn.textContent = 'ADDING...';
    
    try {
      const formData = {
        items: [{
          id: variantId,
          quantity: this.cartQuantity
        }]
      };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        // Success state
        btn.classList.remove('loading');
        btn.classList.add('success');
        btn.textContent = '✓ ADDED';
        
        // Track analytics
        this.trackAddToCart(variantId, this.cartQuantity);
        
        // Trigger cart update event
        document.dispatchEvent(new CustomEvent('cart:updated'));
        
        // Reset after 2 seconds
        setTimeout(() => {
          btn.classList.remove('success');
          btn.disabled = false;
          btn.textContent = originalText;
        }, 2000);
      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (error) {
      console.error('Add to cart error:', error);
      btn.classList.remove('loading');
      btn.disabled = false;
      btn.textContent = originalText;
      alert('Failed to add to cart. Please try again.');
    }
  }

  /**
   * Initialize wishlist functionality
   */
  initWishlist() {
    const wishlistBtns = document.querySelectorAll('.pdp-btn-wishlist, .pdp-product-card__wishlist');
    
    wishlistBtns.forEach(btn => {
      btn.addEventListener('click', (e) => this.toggleWishlist(e));
    });
  }

  /**
   * Toggle wishlist
   */
  toggleWishlist(e) {
    const btn = e.currentTarget;
    btn.classList.toggle('active');
    
    const productId = btn.dataset.productId;
    const isActive = btn.classList.contains('active');
    
    // Store in localStorage
    const wishlist = JSON.parse(localStorage.getItem('wishlist') || '[]');
    
    if (isActive) {
      if (!wishlist.includes(productId)) {
        wishlist.push(productId);
      }
    } else {
      const index = wishlist.indexOf(productId);
      if (index > -1) {
        wishlist.splice(index, 1);
      }
    }
    
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
  }

  /**
   * Initialize fragrance notes accordion
   */
  initFragranceNotes() {
    const headers = document.querySelectorAll('.pdp-section-header');
    
    headers.forEach(header => {
      header.addEventListener('click', () => this.toggleSection(header));
    });
  }

  /**
   * Toggle collapsible section
   */
  toggleSection(header) {
    const content = header.nextElementSibling;
    const toggle = header.querySelector('.pdp-section-toggle');
    
    if (!content || !toggle) return;
    
    const isOpen = content.classList.contains('open');
    
    if (isOpen) {
      content.classList.remove('open');
      toggle.classList.remove('open');
    } else {
      content.classList.add('open');
      toggle.classList.add('open');
    }
  }

  /**
   * Initialize performance bars with animation
   */
  initPerformanceBars() {
    const bars = document.querySelectorAll('.pdp-performance-bar__fill');
    
    // Use IntersectionObserver to animate when visible
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !entry.target.classList.contains('animated')) {
          const fillWidth = entry.target.dataset.width;
          entry.target.style.setProperty('--fill-width', fillWidth);
          entry.target.classList.add('animated');
        }
      });
    }, { threshold: 0.5 });
    
    bars.forEach(bar => observer.observe(bar));
  }

  /**
   * Initialize Frequently Bought Together
   */
  initFBT() {
    const checkboxes = document.querySelectorAll('.pdp-fbt-product__checkbox');
    
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateFBTTotal());
    });
    
    const addBtn = document.querySelector('.pdp-fbt__add-btn');
    if (addBtn) {
      addBtn.addEventListener('click', () => this.addFBTToCart());
    }
    
    // Initialize total
    this.updateFBTTotal();
  }

  /**
   * Update FBT total with bundle discount
   */
  updateFBTTotal() {
    const checkboxes = document.querySelectorAll('.pdp-fbt-product__checkbox:checked');
    let total = 0;
    let count = 0;
    
    checkboxes.forEach(checkbox => {
      const price = parseFloat(checkbox.dataset.price) || 0;
      total += price;
      count++;
    });
    
    // Apply 10% discount
    const discount = total * 0.1;
    const finalTotal = total - discount;
    
    const totalElement = document.querySelector('.pdp-fbt__total');
    const addBtn = document.querySelector('.pdp-fbt__add-btn');
    
    if (totalElement) {
      // Format currency (assumes ZAR)
      const totalPriceElement = totalElement.querySelector('.pdp-fbt__total-price');
    if (totalPriceElement) totalPriceElement.textContent = `R${finalTotal.toFixed(2)}`;
    }
    
    if (addBtn) {
      addBtn.textContent = `Add Selected to Cart (${count} item${count !== 1 ? 's' : ''})`;
      addBtn.disabled = count === 0;
    }
  }

  /**
   * Add FBT products to cart
   */
  async addFBTToCart() {
    const checkboxes = document.querySelectorAll('.pdp-fbt-product__checkbox:checked');
    const items = [];
    
    checkboxes.forEach(checkbox => {
      items.push({
        id: checkbox.dataset.variantId,
        quantity: 1
      });
    });
    
    if (items.length === 0) return;
    
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });
      
      if (response.ok) {
        document.dispatchEvent(new CustomEvent('cart:updated'));
        // Show success feedback
        alert(`${items.length} item${items.length > 1 ? 's' : ''} added to cart!`);
      }
    } catch (error) {
      console.error('FBT add to cart error:', error);
      alert('Failed to add items. Please try again.');
    }
  }

  /**
   * Initialize sticky cart
   */
  initStickyCart() {
    const stickyCart = document.querySelector('.pdp-sticky-cart');
    const trigger = document.querySelector('.pdp-add-to-cart');
    
    if (!stickyCart || !trigger) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          stickyCart.classList.remove('visible');
        } else {
          stickyCart.classList.add('visible');
        }
      });
    }, { threshold: 0 });
    
    observer.observe(trigger);
  }

  /**
   * Initialize activity toast
   */
  initActivityToast() {
    if (!this.productData.recentActivity || this.productData.recentActivity.length === 0) {
      return;
    }
    
    // Show first toast after 15s
    setTimeout(() => {
      this.showActivityToast();
      
      // Then show every 25s
      this.activityToastTimer = setInterval(() => {
        this.showActivityToast();
      }, 25000);
    }, 15000);
  }

  /**
   * Show activity toast
   */
  showActivityToast() {
    const toast = document.querySelector('.pdp-activity-toast');
    if (!toast) return;
    
    const activities = this.productData.recentActivity;
    const randomActivity = activities[Math.floor(Math.random() * activities.length)];
    
    const textElement = toast.querySelector('.pdp-activity-toast__text');
    if (textElement) {
      textElement.innerHTML = `
        <span class="pdp-activity-toast__name">${randomActivity.name}</span> from ${randomActivity.city} purchased this ${randomActivity.time}
      `;
    }
    
    toast.classList.add('visible');
    
    setTimeout(() => {
      toast.classList.remove('visible');
    }, 5000);
  }

  /**
   * Initialize exit intent modal
   */
  initExitIntent() {
    // Check if already shown this session
    if (sessionStorage.getItem('exitIntentShown')) {
      return;
    }
    
    document.addEventListener('mouseleave', (e) => {
      // Only trigger if mouse leaves from top
      if (e.clientY <= 0 && !this.exitIntentShown) {
        this.showExitIntent();
      }
    });
    
    // Close button
    const closeBtn = document.querySelector('.pdp-exit-modal__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hideExitIntent());
    }
    
    // Form submit
    const form = document.querySelector('.pdp-exit-modal__form');
    if (form) {
      form.addEventListener('submit', (e) => this.handleExitIntentSubmit(e));
    }
    
    // Click outside to close
    const modal = document.querySelector('.pdp-exit-modal');
    if (modal) {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.hideExitIntent();
        }
      });
    }
  }

  /**
   * Show exit intent modal
   */
  showExitIntent() {
    const modal = document.querySelector('.pdp-exit-modal');
    if (!modal) return;
    
    modal.classList.add('visible');
    this.exitIntentShown = true;
    sessionStorage.setItem('exitIntentShown', 'true');
  }

  /**
   * Hide exit intent modal
   */
  hideExitIntent() {
    const modal = document.querySelector('.pdp-exit-modal');
    if (modal) {
      modal.classList.remove('visible');
    }
  }

  /**
   * Handle exit intent form submit
   */
  async handleExitIntentSubmit(e) {
    e.preventDefault();
    
    const emailInput = document.querySelector('.pdp-exit-modal__input');
    const email = emailInput?.value;
    
    if (!email) return;
    
    try {
      // Submit to Shopify newsletter
      const response = await fetch('/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'contact[email]': email,
          'contact[tags]': 'newsletter,exit-intent'
        })
      });
      
      if (response.ok) {
        alert('Thanks for subscribing! Check your email for your 10% discount code.');
        this.hideExitIntent();
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      alert('Failed to subscribe. Please try again.');
    }
  }

  /**
   * Render smart badges based on inventory data
   */
  renderBadges() {
    const badgesContainer = document.querySelector('.pdp-badges');
    if (!badgesContainer || !this.productData.inventory) return;
    
    const badges = [];
    const inventory = this.productData.inventory;
    
    // Priority order: Bestseller > Velocity > New
    
    
    // Bestseller (gold)
    if (inventory.isBestseller) {
      badges.push({
        priority: 2,
        html: '<span class="pdp-badge pdp-badge--bestseller">🏷️ BESTSELLER</span>'
      });
    }
    
    // Velocity (orange)
    if (inventory.salesThisWeek > 10) {
      badges.push({
        priority: 3,
        html: `<span class="pdp-badge pdp-badge--velocity">🔥 ${inventory.salesThisWeek} sold this week</span>`
      });
    }
    
    // New arrival (teal)
    if (inventory.daysOld < 30) {
      badges.push({
        priority: 4,
        html: '<span class="pdp-badge pdp-badge--new">✨ NEW ARRIVAL</span>'
      });
    }
    
    // Sort by priority and render (CSS handles max display)
    badges.sort((a, b) => a.priority - b.priority);
    badgesContainer.innerHTML = badges.map(b => b.html).join('');
  }

  /**
   * Track add to cart analytics
   */
  trackAddToCart(variantId, quantity) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          items: [{
            item_id: variantId,
            quantity: quantity
          }]
        }
      });
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.dubaiAromaPDP = new DubaiAromaPDP();
  });
} else {
  window.dubaiAromaPDP = new DubaiAromaPDP();
}
